/**
 * PlanPreview — read-only formatted view of the session plan
 * (issue #437 acceptance criterion #6 + issue #499 parity).
 *
 * The shape and section order come from the shared formatter
 * `formatPlan` (web/src/lib/handoff/formatPlan.ts). Both this
 * in-app Preview and the parent hand-off PDF
 * (`supabase/functions/_shared/parentHandoff.renderPlanSection`,
 * via `supabase/functions/_shared/formatPlan.ts`) render from
 * the same formatter so the SLP sees what the parent receives.
 *
 * The Preview view is switched to via the "Preview" toggle in
 * the section header; toggling back returns the user to the
 * editor with their cursor position preserved (cursor position
 * preservation is handled by the parent — the editor mounts /
 * unmounts on toggle, so React preserves the focused textarea
 * across the mount cycle).
 */
import type { ReactElement } from 'react';
import type { PlanEditorValue } from './PlanEditor';
import {
  formatPlan,
  type FormattedPlanSection,
} from '../lib/handoff/formatPlan';

export interface PlanPreviewProps {
  value: PlanEditorValue;
  /** Student name (printed in the header). */
  studentName?: string | null;
}

/**
 * Render a single section by its discriminated body shape. Kept
 * local (not exported) — the formatter owns the data shape, this
 * function only decides DOM mapping. CSS class names match the
 * existing `index.css` `.plan-preview-*` rules so the stylesheet
 * applies unchanged.
 */
function renderSection(section: FormattedPlanSection): ReactElement {
  if ('paragraphs' in section) {
    return (
      <section className="plan-preview-section">
        <h2>{section.heading}</h2>
        {section.paragraphs.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </section>
    );
  }
  if ('activities' in section) {
    return (
      <section className="plan-preview-section">
        <h2>{section.heading}</h2>
        {section.activities.map((act) => (
          <div
            key={act.number}
            className="plan-preview-activity"
            data-testid={`plan-preview-activity-${act.number}`}
          >
            <h3>
              {act.number}. {act.name} ({act.duration_min} min)
            </h3>
            {act.cues && (
              <p className="plan-preview-detail">
                <strong>Cues:</strong> {act.cues}
              </p>
            )}
            {act.materials && (
              <p className="plan-preview-detail">
                <strong>Materials:</strong> {act.materials}
              </p>
            )}
          </div>
        ))}
      </section>
    );
  }
  return (
    <section className="plan-preview-section">
      <h2>{section.heading}</h2>
      <ol className="plan-preview-prompts">
        {section.list.map((item, idx) => (
          <li key={idx}>{item}</li>
        ))}
      </ol>
    </section>
  );
}

export function PlanPreview({
  value,
  studentName,
}: PlanPreviewProps): ReactElement {
  const formatted = formatPlan(value, {
    ...(studentName != null ? { studentName } : {}),
  });

  return (
    <article className="plan-preview" data-testid="plan-preview">
      <h1 className="plan-preview-title">{formatted.title}</h1>
      {formatted.studentLine && (
        <p className="plan-preview-meta">{formatted.studentLine}</p>
      )}

      {formatted.sections.map((section) => (
        // Heading strings are unique per section so the heading
        // is a stable key; the formatter pins the section order
        // so toggling Preview never reorders DOM.
        <div key={section.heading}>{renderSection(section)}</div>
      ))}
    </article>
  );
}
